// City tags (Ben's Oct 8 concept): coloured labels a player puts on their cities — OFFENSE, CORE, SCHOLARS… — shown on the
// City Overview and used to search, sort and filter the city switcher. Up to five per city; the tags themselves are shared
// by every city in the world. Stored by the Worker in Cloudflare KV (src/state/tags.ts, worker/tags.ts).
import { useEffect, useMemo, useState } from "react";
import { IconCheck, IconClose, IconPencil, IconPlus, IconTrash } from "@/kit/icons";
import { Sheet } from "@/kit/overlays";
import { Button, Disc, cx } from "@/kit/primitives";
import { usePrompts } from "@/state/prompts";
import { useSaveTags, useTagBook } from "@/state/tags";
import {
  MAX_TAGS_PER_CITY, TAG_COLORS, TAG_COLOR_KEYS, TAG_NAME_MAX, citiesWith, cleanName, deleteTag, nameProblem, newTagId, tagsOf, toggleTag,
  type CityTag, type TagBook, type TagColor,
} from "@/game/tagbook";
import { plural } from "@/game/format";

/** A tag as a chip: coloured diamond, NAME, an optional × to take it off. */
export function TagChip({ tag, onRemove, onClick }: { tag: CityTag; onRemove?: () => void; onClick?: () => void }) {
  const style = { ["--tag" as string]: TAG_COLORS[tag.color] };
  const body = (
    <>
      <i className="dia" aria-hidden="true" />
      <span>{tag.name}</span>
    </>
  );
  if (onRemove)
    return (
      <span className="tag-chip" style={style}>
        {body}
        <button type="button" className="x" onClick={onRemove} aria-label={`Take ${tag.name} off this city`}><IconClose /></button>
      </span>
    );
  return onClick ? (
    <button type="button" className="tag-chip" style={style} onClick={onClick} aria-label={`Tag ${tag.name} — edit tags`}>{body}</button>
  ) : (
    <span className="tag-chip" style={style}>{body}</span>
  );
}

/** The hero's chip row: the city's tags and + TAG. */
export function CityTagRow({ wid, vid, onEdit }: { wid: number; vid: number; onEdit: () => void }) {
  const book = useTagBook(wid);
  const tags = book.data ? tagsOf(book.data, vid) : [];
  return (
    <div className="tag-row">
      {tags.map((t) => <TagChip key={t.id} tag={t} onClick={onEdit} />)}
      <button type="button" className="tag-chip add" onClick={onEdit}><IconPlus /> Tag</button>
    </div>
  );
}

export function CityTagsSheet({ open, onClose, wid, vid, cityName }: { open: boolean; onClose: () => void; wid: number; vid: number; cityName: string }) {
  const book = useTagBook(wid);
  const save = useSaveTags(wid);
  const [draft, setDraft] = useState<TagBook | null>(null);
  const [name, setName] = useState("");
  const [color, setColor] = useState<TagColor>("steel");
  const [editing, setEditing] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState<TagColor>("gold");

  // a fresh draft from the stored book each time the sheet opens
  useEffect(() => {
    if (open) {
      setDraft(book.data ?? null);
      setEditing(null);
      setName("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, book.isFetched]);

  const d = draft ?? book.data ?? { tags: [], cities: {} };
  const onCity = tagsOf(d, vid);
  const dirty = useMemo(() => JSON.stringify(d) !== JSON.stringify(book.data ?? { tags: [], cities: {} }), [d, book.data]);
  const problem = name.trim() ? nameProblem(d, name) : null;
  const toast = (t: string) => usePrompts.getState().toast(t);

  const toggle = (id: string) => {
    const next = toggleTag(d, vid, id);
    if (next === false) toast(`Up to ${MAX_TAGS_PER_CITY} tags on a city — take one off first.`);
    else setDraft(next);
  };
  const create = () => {
    if (nameProblem(d, name)) return;
    const tag: CityTag = { id: newTagId(), name: cleanName(name), color };
    const withTag: TagBook = { ...d, tags: [...d.tags, tag] };
    const on = toggleTag(withTag, vid, tag.id);
    setDraft(on === false ? withTag : on);
    if (on === false) toast(`Made ${tag.name}. This city already wears ${MAX_TAGS_PER_CITY} tags, so it isn't on it.`);
    setName("");
  };
  const startEdit = (t: CityTag) => {
    setEditing(t.id);
    setEditName(t.name);
    setEditColor(t.color);
  };
  const finishEdit = () => {
    if (!editing) return;
    const p = nameProblem(d, editName, editing);
    if (p) return toast(p);
    setDraft({ ...d, tags: d.tags.map((t) => (t.id === editing ? { ...t, name: cleanName(editName), color: editColor } : t)) });
    setEditing(null);
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      label="City tags"
      title={
        <div className="tags-head">
          <div>
            <h3>City tags</h3>
            <div className="sub">{cityName} · <b className="gold">{onCity.length}</b> of {MAX_TAGS_PER_CITY} tags</div>
          </div>
          <Disc size="sm" label="Close" onClick={onClose}><IconClose /></Disc>
        </div>
      }
    >
      <div className="tags-body">
        <div className="tags-label">On this city</div>
        <div className="tag-row">
          {onCity.map((t) => <TagChip key={t.id} tag={t} onRemove={() => toggle(t.id)} />)}
          {!onCity.length && <span className="muted tags-empty">No tags on this city yet — tick one below or make a new one.</span>}
        </div>

        <div className="tags-label split"><span>Your tags · World {wid}</span>{d.tags.length > 0 && <em>tap to add or remove</em>}</div>
        <div className="tags-list">
          {d.tags.map((t) =>
            editing === t.id ? (
              <div key={t.id} className="tag-line editing" style={{ ["--tag" as string]: TAG_COLORS[editColor] }}>
                <input className="input" value={editName} maxLength={TAG_NAME_MAX} onChange={(e) => setEditName(e.target.value)} aria-label="Tag name" autoFocus onKeyDown={(e) => e.key === "Enter" && finishEdit()} />
                <Swatches value={editColor} onChange={setEditColor} />
                <div className="tag-edit-acts">
                  <button type="button" className="link crimtxt" onClick={() => { setDraft(deleteTag(d, t.id)); setEditing(null); }}><IconTrash /> Delete tag</button>
                  <Button size="sm" kind="steel" onClick={() => setEditing(null)}>Cancel</Button>
                  <Button size="sm" kind="gold" onClick={finishEdit}>Done</Button>
                </div>
              </div>
            ) : (
              <div key={t.id} className={cx("tag-line", onCity.some((x) => x.id === t.id) && "on")} style={{ ["--tag" as string]: TAG_COLORS[t.color] }}>
                <button type="button" className="tag-hit" onClick={() => toggle(t.id)} aria-pressed={onCity.some((x) => x.id === t.id)} aria-label={`${t.name} on ${cityName}`}>
                  <i className="dia" aria-hidden="true" />
                  <span className="nm">{t.name}</span>
                  <span className="n">{plural(citiesWith(d, t.id), "city", "cities")}</span>
                </button>
                <button type="button" className="tag-pencil" onClick={() => startEdit(t)} aria-label={`Edit ${t.name}`}><IconPencil /></button>
                <button type="button" className="tag-box" onClick={() => toggle(t.id)} aria-hidden="true" tabIndex={-1}>{onCity.some((x) => x.id === t.id) && <IconCheck />}</button>
              </div>
            ),
          )}
          {!d.tags.length && <div className="muted tags-empty">No tags in World {wid} yet.</div>}
        </div>

        <div className="tags-label">New tag</div>
        <div className="tag-new">
          <input className="input" placeholder="Name, e.g. Siege or Near Grimhold" value={name} maxLength={TAG_NAME_MAX} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()} aria-label="New tag name" />
          <Button kind="steel" size="md" onClick={create} disabled={!name.trim() || !!problem}>Create</Button>
        </div>
        {problem && <div className="c-note left">{problem}</div>}
        <div className="tag-colours"><span className="tags-label inline">Colour</span><Swatches value={color} onChange={setColor} /></div>

        <Button kind="crimson" wide disabled={!dirty} busy={save.isPending} onClick={async () => { await save.mutateAsync(d).then(onClose, () => undefined); }}>
          Save tags
        </Button>
        <div className="c-note">Tags also search, sort and filter your city switcher. Up to {MAX_TAGS_PER_CITY} per city.</div>
      </div>
    </Sheet>
  );
}

function Swatches({ value, onChange }: { value: TagColor; onChange: (c: TagColor) => void }) {
  return (
    <span className="swatches" role="radiogroup" aria-label="Tag colour">
      {TAG_COLOR_KEYS.map((c) => (
        <button key={c} type="button" role="radio" aria-checked={value === c} aria-label={c} className={cx("swatch", value === c && "on")} style={{ background: TAG_COLORS[c] }} onClick={() => onChange(c)} />
      ))}
    </span>
  );
}
